import React, { useEffect, useMemo, useState } from 'react';
import { defaultChecklists } from '../utils/defaults';
import type { ChecklistGroup, ChecklistItem } from '../types/checklist';

interface UseChecklistStateOptions {
  storageKey?: string;
}

export function useChecklistState(options: UseChecklistStateOptions = {}) {
  const { storageKey = 'checklistProgress' } = options;

  // Limpar dados antigos do localStorage ao inicializar
  React.useEffect(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {}
  }, [storageKey]);

  const [checklists, setChecklists] = useState<ChecklistGroup[]>(() => {
    // Sempre começar com os defaults, garantindo que etapas manuais estejam desmarcadas
    return defaultChecklists;
  });

  // Removido localStorage para evitar persistência de dados antigos
  // O usuário deve marcar manualmente as etapas 2 e 3

  const totals = useMemo(() => {
    const groupsTotal = checklists.length;
    const groupsCompleted = checklists.filter(g => g.items.every(i => i.checked)).length;

    let itemsTotal = 0;
    let itemsCompleted = 0;
    checklists.forEach(g => {
      itemsTotal += g.items.length;
      itemsCompleted += g.items.filter(i => i.checked).length;
    });

    const progress = itemsTotal > 0 ? (itemsCompleted / itemsTotal) * 100 : 0;
    const allChecked = itemsTotal > 0 && itemsCompleted === itemsTotal;

    return { groupsTotal, groupsCompleted, itemsTotal, itemsCompleted, progress, allChecked };
  }, [checklists]);

  const toggleItem = (groupIndex: number, itemIndex: number) => {
    setChecklists(prev =>
      prev.map((g, gi) =>
        gi !== groupIndex
          ? g
          : {
              ...g,
              items: g.items.map((it, ii) => (ii === itemIndex ? { ...it, checked: !it.checked } : it)),
            },
      ),
    );
  };

  const setItemChecked = (groupIndex: number, itemIndex: number, checked: boolean) => {
    setChecklists(prev =>
      prev.map((g, gi) =>
        gi !== groupIndex
          ? g
          : { ...g, items: g.items.map((it, ii) => (ii === itemIndex ? { ...it, checked } : it)) },
      ),
    );
  };

  const resetAll = () => setChecklists(defaultChecklists);

  const getItem = (groupIndex: number, itemIndex: number): ChecklistItem | undefined =>
    checklists[groupIndex]?.items[itemIndex];

  return { checklists, setChecklists, totals, toggleItem, setItemChecked, resetAll, getItem } as const;
}